import { createFileRoute, redirect, useNavigate, useSearch } from "@tanstack/react-router";
import { z } from "zod";
import { AuthLayout } from "@/features/auth/components/AuthLayout";
import { getAccessKey } from "@/lib/api/client";
import { useAuth } from "@/providers/auth-provider";
import { CliAuthorizePanel } from "@/features/cli/components/CliAuthorizePanel";
import { useCliAuthorization } from "@/features/cli/hooks/useCli";

const searchSchema = z.object({
  request: z.string().catch(""),
});

function authorizePath(requestId: string): string {
  return `/cli/authorize?request=${encodeURIComponent(requestId)}`;
}

export const Route = createFileRoute("/cli/authorize")({
  validateSearch: searchSchema,
  // Its own guard rather than the _authenticated layout: this page is not part
  // of the dashboard shell, and it has to come back here after signing in.
  beforeLoad: ({ search }) => {
    if (!getAccessKey()) {
      throw redirect({ to: "/login", search: { redirect: authorizePath(search.request) } });
    }
  },
  component: CliAuthorizePage,
});

function CliAuthorizePage() {
  const { request } = useSearch({ from: "/cli/authorize" });
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { data, isLoading, error } = useCliAuthorization(request);

  const reauthenticate = () => {
    // Signing in again is what produces a fresh session, so the stored key has
    // to go, through the provider so its state goes with it.
    logout();
    navigate({ to: "/login", search: { redirect: authorizePath(request) } });
  };

  // Deliberately not wrapped in MfaSetupGate. The gate's full-screen takeover would
  // hide who is asking and remove the deny button, and denying a request you did not
  // start needs no second factor. The panel refuses approval instead, and the server
  // refuses it again regardless of what this page renders.
  return (
    <AuthLayout>
      <CliAuthorizePanel
        authorization={data}
        isLoading={isLoading}
        error={error ?? null}
        onReauthenticate={reauthenticate}
      />
    </AuthLayout>
  );
}
